import type {
  ComponentValueResponse,
  SaveSustainabilityDecisionRequest,
  SustainabilityComponentInput,
} from "@repo/shared/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as sustainabilityApi from "@/lib/api/flow-instances/sustainability";
import { useInvalidateFlowCompletion } from "./use-flow-completion";
import { useInvalidatePortfolioSnapshots } from "./use-portfolio-snapshots";
import { useInvalidateDynamicSync } from "./use-portfolios";

export const sustainabilityKeys = {
  flowInstanceValue: (id: string | null) =>
    ["flowInstances", id, "sustainability", "flow-instance-value"] as const,
  portfolioState: (id: string | null, portfolioId: string | null) =>
    ["flowInstances", id, "sustainability", "portfolio-state", portfolioId] as const,
  allPortfolioStates: (id: string | null) =>
    ["flowInstances", id, "sustainability", "portfolio-state"] as const,
};

export function useSustainabilityFlowInstanceValue(id: string | null) {
  const { data, error, isLoading } = useQuery({
    queryKey: sustainabilityKeys.flowInstanceValue(id),
    queryFn: () => sustainabilityApi.getFlowInstanceValue(id!),
    enabled: !!id,
  });
  return {
    inputData: data?.inputData ?? null,
    calculatedData: data?.calculatedData ?? null,
    error,
    isLoading,
  };
}

export function useSaveSustainability(instanceId: string) {
  const queryClient = useQueryClient();
  const invalidateCompletion = useInvalidateFlowCompletion();
  const invalidatePortfolioSnapshots = useInvalidatePortfolioSnapshots();
  const invalidateDynamicSync = useInvalidateDynamicSync();
  const queryKey = sustainabilityKeys.flowInstanceValue(instanceId);
  return useMutation({
    mutationFn: (input: SustainabilityComponentInput) =>
      sustainabilityApi.saveFlowInstanceValue(instanceId, input),
    // Optimistic input echo — the widget re-initializes from this cache on
    // remount (wizard tab switches), mirroring the financial-situation hook.
    onMutate: async (input) => {
      await queryClient.cancelQueries({ queryKey });
      const previousData = queryClient.getQueryData<ComponentValueResponse>(queryKey);
      queryClient.setQueryData<ComponentValueResponse>(queryKey, (old) => ({
        inputData: JSON.stringify(input),
        calculatedData: old?.calculatedData ?? null,
      }));
      return { previousData };
    },
    onError: (_err, _input, context) => {
      if (context?.previousData !== undefined) {
        queryClient.setQueryData(queryKey, context.previousData);
      }
    },
    onSuccess: (result) => {
      if (result.success && result.inputData !== undefined) {
        queryClient.setQueryData(queryKey, {
          inputData: result.inputData,
          calculatedData: result.calculatedData,
        });
        // The component gates Submit (gate + granulations + alignment).
        invalidateCompletion(instanceId);

        // QP-179 — the save re-ran the matching cascade server-side: refresh
        // the per-portfolio sustainability snapshots + every goal's state
        // (a stale decision may have been voided).
        const sync = result.sustainabilitySync;
        if (sync?.changed) {
          for (const hint of sync.refreshHints) {
            invalidatePortfolioSnapshots(hint.portfolioId, hint);
          }
        }
        queryClient.invalidateQueries({
          queryKey: sustainabilityKeys.allPortfolioStates(instanceId),
        });
        // QP-185 — the save regenerated dynamic portfolios.
        invalidateDynamicSync(result.dynamicSync);
      }
    },
  });
}

export function usePortfolioSustainabilityState(
  instanceId: string | null,
  portfolioId: string | null,
) {
  const { data, error, isLoading } = useQuery({
    queryKey: sustainabilityKeys.portfolioState(instanceId, portfolioId),
    queryFn: () => sustainabilityApi.getPortfolioSustainabilityState(instanceId!, portfolioId!),
    enabled: !!instanceId && !!portfolioId,
  });
  return { state: data ?? null, error, isLoading };
}

export function useSavePortfolioSustainabilityDecision(instanceId: string, portfolioId: string) {
  const queryClient = useQueryClient();
  const invalidateCompletion = useInvalidateFlowCompletion();
  return useMutation({
    mutationFn: (request: SaveSustainabilityDecisionRequest) =>
      sustainabilityApi.savePortfolioSustainabilityDecision(instanceId, portfolioId, request),
    onSuccess: (result) => {
      if (result.success && result.state) {
        queryClient.setQueryData(
          sustainabilityKeys.portfolioState(instanceId, portfolioId),
          result.state,
        );
        // The decision flips the portfolio's effective status → Submit gate changes.
        invalidateCompletion(instanceId);
      }
    },
  });
}
